import { useEffect, useRef, useState, useImperativeHandle, forwardRef } from "react";
import { Loader2, CreditCard, Lock } from "lucide-react";

/**
 * Clover hosted-iframe card form. Loads Clover's `sdk.js` once, then mounts
 * three iframe-backed fields (card number, expiry, cvv) into our containers.
 *
 * The parent component should hold a ref to this and call `.tokenize()` which
 * returns either `{ token: 'clv_…' }` on success or `{ error: '…' }`.
 *
 * Props:
 *   config: { enabled, env, public_key, sdk_url } from /api/clover/config
 */
const CloverCardForm = forwardRef(function CloverCardForm({ config }, ref) {
  const cloverRef = useRef(null);
  const elementsRef = useRef({});
  const [ready, setReady] = useState(false);
  const [fieldError, setFieldError] = useState("");

  useEffect(() => {
    if (!config?.enabled || !config.public_key || !config.sdk_url) return;
    // Idempotency guard — React StrictMode mounts effects twice in dev.
    // Clover's elements can't be remounted onto the same DOM nodes without
    // duplicate iframes appearing.
    if (cloverRef.current) return;

    const SCRIPT_ID = "clover-sdk-js";
    const existing = document.getElementById(SCRIPT_ID);

    const init = () => {
      if (!window.Clover) return;
      if (cloverRef.current) return;
      try {
        const clover = new window.Clover(config.public_key, {
          merchantId: config.merchant_id,
        });
        cloverRef.current = clover;
        const elements = clover.elements();
        const styles = {
          body: { fontFamily: "Inter, system-ui, sans-serif", fontSize: "15px" },
          input: {
            fontSize: "15px",
            color: "#0E0E1A",
            "::placeholder": { color: "#9CA3AF" },
          },
        };
        const cardNumber = elements.create("CARD_NUMBER", styles);
        const cardDate = elements.create("CARD_DATE", styles);
        const cardCvv = elements.create("CARD_CVV", styles);
        const cardZip = elements.create("CARD_POSTAL_CODE", styles);

        // Clear any pre-existing children (StrictMode dev double-mount guard)
        ["clover-card-number", "clover-card-date", "clover-card-cvv", "clover-card-zip"].forEach(
          (id) => {
            const el = document.getElementById(id);
            if (el) el.innerHTML = "";
          }
        );

        cardNumber.mount("#clover-card-number");
        cardDate.mount("#clover-card-date");
        cardCvv.mount("#clover-card-cvv");
        cardZip.mount("#clover-card-zip");

        const onChange = (event) => {
          if (event?.CARD_NUMBER?.error) {
            setFieldError(event.CARD_NUMBER.error);
          } else if (event?.CARD_DATE?.error) {
            setFieldError(event.CARD_DATE.error);
          } else if (event?.CARD_CVV?.error) {
            setFieldError(event.CARD_CVV.error);
          } else {
            setFieldError("");
          }
        };
        cardNumber.addEventListener("change", onChange);
        cardDate.addEventListener("change", onChange);
        cardCvv.addEventListener("change", onChange);

        elementsRef.current = { cardNumber, cardDate, cardCvv, cardZip };
        setReady(true);
      } catch (e) {
        console.error("Clover init failed:", e);
        setFieldError("Could not load card form. Please refresh.");
      }
    };

    if (window.Clover) {
      init();
    } else if (existing) {
      existing.addEventListener("load", init, { once: true });
    } else {
      const s = document.createElement("script");
      s.id = SCRIPT_ID;
      s.src = config.sdk_url;
      s.async = true;
      s.onload = init;
      s.onerror = () => setFieldError("Could not load Clover. Check your internet and refresh.");
      document.head.appendChild(s);
    }

    return () => {
      // Don't tear down the SDK script — Clover does not support remount on
      // the same elements; recreating on every mount triggers iframe warnings.
    };
  }, [config?.enabled, config?.public_key, config?.sdk_url, config?.merchant_id]);

  useImperativeHandle(
    ref,
    () => ({
      tokenize: async () => {
        if (!cloverRef.current) {
          return { error: "Card form not ready. Please wait a moment and try again." };
        }
        try {
          const result = await cloverRef.current.createToken();
          if (result?.errors) {
            const firstErr =
              result.errors?.CARD_NUMBER?.error ||
              result.errors?.CARD_DATE?.error ||
              result.errors?.CARD_CVV?.error ||
              "Please check your card details.";
            return { error: firstErr };
          }
          if (!result?.token) {
            return { error: "Couldn't read card. Please check the details." };
          }
          return { token: result.token };
        } catch (e) {
          console.error("createToken failed:", e);
          return { error: e?.message || "Card tokenization failed." };
        }
      },
      isReady: () => ready,
    }),
    [ready]
  );

  if (!config?.enabled) {
    return null;
  }

  return (
    <div
      data-testid="clover-card-form"
      className="rounded-2xl bg-white border border-[#E7E5E4] p-5 space-y-4"
    >
      <div className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[#0E0E1A] font-bold">
        <CreditCard className="w-3.5 h-3.5" />
        Pay securely with card
      </div>

      <div>
        <label className="block text-[11px] font-bold tracking-wide text-[#57534E] mb-1.5 uppercase">
          Card number
        </label>
        <div
          id="clover-card-number"
          className="min-h-[44px] rounded-xl border border-[#E7E5E4] bg-white px-3 py-2 focus-within:border-[#C9853E] focus-within:ring-2 focus-within:ring-[#C9853E]/20 transition-all"
        />
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="block text-[11px] font-bold tracking-wide text-[#57534E] mb-1.5 uppercase">
            Expiry
          </label>
          <div
            id="clover-card-date"
            className="min-h-[44px] rounded-xl border border-[#E7E5E4] bg-white px-3 py-2 focus-within:border-[#C9853E] focus-within:ring-2 focus-within:ring-[#C9853E]/20 transition-all"
          />
        </div>
        <div>
          <label className="block text-[11px] font-bold tracking-wide text-[#57534E] mb-1.5 uppercase">
            CVV
          </label>
          <div
            id="clover-card-cvv"
            className="min-h-[44px] rounded-xl border border-[#E7E5E4] bg-white px-3 py-2 focus-within:border-[#C9853E] focus-within:ring-2 focus-within:ring-[#C9853E]/20 transition-all"
          />
        </div>
        <div>
          <label className="block text-[11px] font-bold tracking-wide text-[#57534E] mb-1.5 uppercase">
            Postal
          </label>
          <div
            id="clover-card-zip"
            className="min-h-[44px] rounded-xl border border-[#E7E5E4] bg-white px-3 py-2 focus-within:border-[#C9853E] focus-within:ring-2 focus-within:ring-[#C9853E]/20 transition-all"
          />
        </div>
      </div>

      {fieldError ? (
        <p
          className="text-xs text-red-600 font-medium"
          data-testid="clover-field-error"
        >
          {fieldError}
        </p>
      ) : null}

      <div className="flex items-center gap-2 text-[11px] text-[#57534E] pt-1">
        <Lock className="w-3 h-3" />
        Card details are sent directly to Clover. Rivaaz never sees your card number.
        {!ready && (
          <span className="ml-auto inline-flex items-center gap-1 text-[#57534E]">
            <Loader2 className="w-3 h-3 animate-spin" /> Loading…
          </span>
        )}
      </div>
    </div>
  );
});

export default CloverCardForm;
